import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  getArticleComments,
  deleteArticleComment
} from '../../services/firestoreService';
import { handleError, handleSuccess, handleWarning } from '../../services/errorHandlingService';
import { shareOnLinkedIn, isLinkedInConnected } from '../../services/linkedinService';
import { serverTimestamp } from 'firebase/firestore';

const NewsManagement = () => {
  const { hasRole } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleComments, setArticleComments] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'News',
    author: '',
    image: '',
    featured: false,
    status: 'published',
    postToLinkedIn: false
  });

  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (isAdmin) {
      loadArticles();
    }
  }, [isAdmin]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const articlesData = await getArticles(50, null, true); // Get all articles for admin view
      setArticles(articlesData);
      console.log('Loaded articles for admin:', articlesData);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadArticleComments = async (articleId) => {
    try {
      const comments = await getArticleComments(articleId);
      setArticleComments(comments);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const articleData = {
        ...formData,
        publishedAt: serverTimestamp(),
        likeCount: 0,
        commentCount: 0,
        views: 0
      };

      let articleId;
      if (editingArticle) {
        await updateArticle(editingArticle.id, articleData);
        articleId = editingArticle.id;
      } else {
        articleId = await createArticle(articleData);
      }

      // Post to LinkedIn if requested
      if (formData.postToLinkedIn && formData.status === 'published') {
        try {
          const articleUrl = `${window.location.origin}/news/${articleId}`;
          const linkedInData = {
            title: formData.title,
            excerpt: formData.excerpt,
            articleUrl: articleUrl,
            imageUrl: formData.image ? `${window.location.origin}${formData.image}` : null
          };

          shareOnLinkedIn(linkedInData);
          alert('Article saved and LinkedIn share dialog opened!');
        } catch (linkedInError) {
          console.error('Error posting to LinkedIn:', linkedInError);
          alert('Article saved successfully, but LinkedIn posting failed. You can manually share the article.');
        }
      }

      setFormData({
        title: '',
        excerpt: '',
        content: '',
        category: 'News',
        author: '',
        image: '',
        featured: false,
        status: 'published',
        postToLinkedIn: false
      });
      setShowCreateForm(false);
      setEditingArticle(null);
      await loadArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Error saving article. Please try again.');
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title || '',
      excerpt: article.excerpt || '',
      content: article.content || '',
      category: article.category || 'News',
      author: article.author || '',
      image: article.image || '',
      featured: article.featured || false,
      status: article.status || 'published',
      postToLinkedIn: false // Don't auto-check for edits
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (articleId) => {
    if (window.confirm('Are you sure you want to delete this article? This will also delete all comments and cannot be undone.')) {
      try {
        await deleteArticle(articleId);
        await loadArticles();
        handleSuccess('Article deleted successfully.');
      } catch (error) {
        handleError(error, {
          context: {
            component: 'NewsManagement',
            action: 'deleteArticle',
            articleId
          }
        });
      }
    }
  };

  const handleDeleteComment = async (commentId, articleId) => {
    if (window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      try {
        await deleteArticleComment(commentId, articleId);
        await loadArticleComments(selectedArticle.id);
        handleSuccess('Comment deleted successfully.');
      } catch (error) {
        handleError(error, {
          context: {
            component: 'NewsManagement',
            action: 'deleteComment',
            commentId,
            articleId
          }
        });
      }
    }
  };

  const handleViewComments = async (article) => {
    setSelectedArticle(article);
    await loadArticleComments(article.id);
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Access denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">News Management</h1>
        <p className="mt-2 text-gray-600">Manage news articles and comments</p>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex space-x-4">
        <button
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingArticle(null);
            setFormData({
              title: '',
              excerpt: '',
              content: '',
              category: 'News',
              author: '',
              image: '',
              featured: false,
              status: 'published',
              postToLinkedIn: false
            });
          }}
          className="bg-apn-blue text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          {showCreateForm ? 'Cancel' : 'Create New Article'}
        </button>
        
        <button
          onClick={loadArticles}
          className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">
            {editingArticle ? 'Edit Article' : 'Create New Article'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Author *
                </label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="News">News</option>
                  <option value="Feature">Feature</option>
                  <option value="Announcement">Announcement</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              
              <div className="flex flex-col space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Featured Article</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.postToLinkedIn}
                    onChange={(e) => setFormData({ ...formData, postToLinkedIn: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Share on LinkedIn</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image URL
              </label>
              <input
                type="text"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                placeholder="/images/news/article-image.jpg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Excerpt *
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                rows="3"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                rows="10"
                required
              />
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-apn-blue text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                {editingArticle ? 'Update Article' : 'Create Article'}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingArticle(null);
                }}
                className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Articles List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Articles ({articles.length})
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Article
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {articles.map((article) => (
                <tr key={article.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {article.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          By {article.author} • {article.publishedAt?.toDate?.()?.toLocaleDateString() || article.date}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      article.category === 'Feature' ? 'bg-purple-100 text-purple-800' :
                      article.category === 'News' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {article.category}
                    </span>
                    {article.featured && (
                      <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Featured
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      article.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {article.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>Views: {article.views || 0}</div>
                    <div>Likes: {article.likeCount || 0}</div>
                    <div>Comments: {article.commentCount || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(article)}
                      className="text-apn-blue hover:text-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleViewComments(article)}
                      className="text-green-600 hover:text-green-700"
                    >
                      Comments ({article.commentCount || 0})
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Comments Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Comments for "{selectedArticle.title}"
                </h3>
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {articleComments.length > 0 ? (
                  <div className="space-y-4">
                    {articleComments.map((comment) => (
                      <div key={comment.id} className="border-b border-gray-200 pb-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-medium text-gray-900">{comment.authorName}</span>
                            <span className="text-sm text-gray-500 ml-2">
                              {comment.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleDeleteComment(comment.id, selectedArticle.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                        <p className="text-gray-700">{comment.content}</p>
                        <div className="text-sm text-gray-500 mt-1">
                          Likes: {comment.likeCount || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No comments yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
